// ─── Comp dedup utility ─────────────────────────────────────────────────────
// Address normalization + dedup-key helpers shared by:
//   - Extraction staging UI (skip rows already in global comps / already
//     assigned to this deal)
//   - Manual form save (warn if an identical comp already exists)
//   - Library picker (visually flag already-assigned rows)
//
// Goal: forgive minor formatting differences (case, trailing punctuation,
// unit/suite numbers) without collapsing genuinely different addresses.

// Strip suite/unit/floor designators — they routinely differ between sources
// even though the underlying property is the same.
const SUITE_RE = /\b(suite|ste|unit|bldg|floor|fl)\s*[#-]?\s*[a-z0-9-]+\b/gi;

// Compact whitespace, lowercase, strip most punctuation (keep alphanumerics +
// spaces). After: "1234 Main St, Suite 100, Dallas, TX 75201" → "1234 main st dallas tx 75201"
export function normalizeAddress(raw) {
  if (!raw) return "";
  return String(raw)
    .toLowerCase()
    .replace(SUITE_RE, " ")
    .replace(/[.,#]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Dedup key for a sale or lease comp. Address + comp_date is enough — two
// sales at the same address on the same date are functionally the same trade.
export function compKey(comp) {
  if (!comp) return "";
  const addr = normalizeAddress(comp.address);
  const date = comp.comp_date || "";
  return `${addr}|${date}`;
}

// Dedup key for a rent-roll row. Mirrors the database's own key - see
// migration 027 and RENT_ROLL_KEY in supabase.js.
//
// It used to be address + tenant + suite_id, and subject_address and suite_id
// are both NULL on essentially every row, so the key collapsed to the tenant
// name alone. That is worse than useless here: a rent roll legitimately holds
// two VACANT rows of different sizes, and this would have called the second
// one a duplicate of the first and dropped it. Size, start date and the source
// row are what tell those apart.
export function rentRollKey(row) {
  if (!row) return "";
  const addr = normalizeAddress(row.subject_address || row.address);
  const tenant = (row.tenant || "").toLowerCase().trim();
  const suite = String(row.suite_id || row.unit_label || "").toLowerCase().trim();
  const sf = row.sf ?? row.suite_sf ?? "";
  const start = row.lease_start || "";
  const srcRow = row.source_row ?? "";
  return `${addr}|${tenant}|${suite}|${start}|${sf}|${srcRow}`;
}

// Convenience: builds a Set of keys for fast lookup
export function buildCompKeySet(comps) {
  const set = new Set();
  for (const c of comps || []) set.add(compKey(c));
  return set;
}

export function buildRentRollKeySet(rows) {
  const set = new Set();
  for (const r of rows || []) set.add(rentRollKey(r));
  return set;
}

// Cross-reference key for de-duping a lease COMP against a rent-roll row.
// Both inputs collapse to (normalized address, lowercased tenant) — used to
// catch the case where an OM lists the same subject-property tenant in
// BOTH the rent roll AND the lease comps section. Returns "" if either
// half is missing so we don't collapse partial rows.
export function leaseTenantAddrKey({ address, tenant }) {
  const addr = normalizeAddress(address);
  const t = (tenant || "").toLowerCase().trim();
  if (!addr || !t) return "";
  return `${addr}|${t}`;
}
